import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.api';
import { TranslationModuleEnum } from 'src/app/utils/translation-modules.enum';

@Component({
  selector: 'app-reset-password-modal',
  templateUrl: './reset-password-modal.component.html',
  styleUrls: ['./reset-password-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordModalComponent implements OnInit, OnDestroy {
  @Input() isVisible = false;
  @Input() token = '';
  @Input() email = '';
  @Input() isRequiredPasswordChange = false;
  @Output() onToggleModal = new EventEmitter<boolean>();
  @Output() onSubmitEvent = new EventEmitter<string>();

  translationModulesEnum = TranslationModuleEnum;
  form: FormGroup;
  isBtnLoading = false;
  isNewPasswordDisplayed = false;
  isConfirmPasswordDisplayed = false;

  private endSub$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      newPassword: [
        null,
        [Validators.required, Validators.minLength(8)],
      ],
      confirmPassword: [
        null,
        [Validators.required, this.matchValues('newPassword')],
      ],
    });

    // revalidate confirm when new password changes
    this.form.controls['newPassword'].valueChanges
      .pipe(takeUntil(this.endSub$))
      .subscribe(() =>
        this.form.controls['confirmPassword'].updateValueAndValidity(),
      );
  }

  private matchValues(
    matchTo: string,
  ): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.parent?.value &&
        control.value !== control.parent.controls[matchTo].value
        ? { mismatch: true }
        : null;
    };
  }

  onSubmit() {
    if (this.form.invalid) return;
    this.isBtnLoading = true;
    this.cdr.markForCheck();

    const resetRequest = this.isRequiredPasswordChange
      ? this.authService.resetPasswordForRequiredChange({
          token: this.token,
          email: this.email,
          password: this.form.value.newPassword,
        })
      : this.authService.resetPassword({
          token: this.token,
          email: this.email,
          password: this.form.value.newPassword,
        });

    resetRequest
      .pipe(takeUntil(this.endSub$))
      .subscribe(
        () => {
          this.isBtnLoading = false;
          this.toastr.success(
            this.translate.instant('shared.resetPasswordModal.successMsg'),
          );
          this.onSubmitEvent.emit(this.form.value.newPassword);
          this.close();
          this.cdr.markForCheck();
        },
        (err: any) => {
          this.isBtnLoading = false;
          const msg =
            err?.error?.msg ||
            err?.error?.message ||
            err?.message ||
            this.translate.instant('shared.resetPasswordModal.errorMsg');
          this.toastr.error(msg);
          this.cdr.markForCheck();
        },
      );
  }

  toggleNewPassword() {
    this.isNewPasswordDisplayed = !this.isNewPasswordDisplayed;
  }

  toggleConfirmPassword() {
    this.isConfirmPasswordDisplayed = !this.isConfirmPasswordDisplayed;
  }

  close() {
    this.isVisible = false;
    this.form.reset();
    this.onToggleModal.emit(false);
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.endSub$.next();
    this.endSub$.complete();
  }
}
